import { router, useLocalSearchParams } from 'expo-router';
import { useAtomValue } from 'jotai';
import { TabPanel, TabView } from 'primereact/tabview';
import React, { useMemo } from 'react';
import { type StackProps, YStack } from 'smoothie';

import { PageContentContainer } from '../../../common/components/GroveContainers';
import { CheckInSessionsTable } from '../../../guidance-sessions/components/CheckInSessionsTable';
import { GuidanceSessionsTable } from '../../../guidance-sessions/components/GuidanceSessionsTable';
import { MemberProfile } from '../../../member-profile/components/MemberProfile';
import { MoneyMapTable } from '../../../money-map/components/MoneyMapTable';
import { adminUserAtom } from '../../entityAtoms';
import { AdminUserRole, type MembershipDetails } from '../../entityTypes';
import {
    membershipHasHadIncomeSplitV0,
    membershipHasHadIncomeSplitWithTies,
    membershipHasHadPaycheckSplit,
    showIncomeSplit,
} from '../membershipUtils';
import { MembershipRecurringTransfers } from './banking/MemberRecurringTransfers';
import { MembershipBankingOps } from './banking/MembershipBankingOps';
import { BillsDevToolsTabPanel } from './BillsDevToolsTabPanel';
import ComplianceNotesTab from './compliance/MembershipComplianceNotesTab';
import { ConversationalMMSection } from './ConversationalMMSection';
import { DirectDepositSwitchTabPanel } from './directDepositSwitch/MembershipDirectDepositSwitch';
import { MembershipGoalsInfo } from './goals/MembershipGoalsInfo';
import { AccountsTabPanel } from './MembershipAccountsInfo';
import { CashFlowTabPanel } from './MembershipCashFlowInfo';
import { MembershipDocumentVaultTabPanel } from './MembershipDocumentVaultTabPanel';
import { MembershipUserEventsTabPanel } from './MembershipUserEventsTabPanel';
import { IncomeSplitV2TabPanel } from './paycheckSplit/IncomeSplitV2TabPanel';
import { IncomeSplitTabPanel } from './paycheckSplit/MembershipIncomeSplit';
import { TransactionSearchSection } from './TransactionSearchSection';

interface MembershipDetailsTabsProps extends StackProps {
    membershipDetails: MembershipDetails | null;
}

export const MembershipDetailsTabs = ({
    membershipDetails,
    ...stackProps
}: MembershipDetailsTabsProps) => {
    const { dt: selectedTabIndex } = useLocalSearchParams<{ dt: string }>();
    const adminUser = useAtomValue(adminUserAtom);
    const membershipPermissions = adminUser.authorization.selectedMembership;
    const activeIndex = parseInt(selectedTabIndex, 10) || 0;
    const canShowGuideMessaging =
        membershipDetails != null &&
        adminUser.authorization.guideMessaging.canUse &&
        membershipDetails.guide?.sendbirdUserId != null &&
        membershipDetails.guide.sendbirdUserId ===
            adminUser.guide?.sendbirdUserId;

    const billsDevToolsTabIndex = useMemo(() => {
        if (!adminUser.authorization.betaTransactionFeatures.canUse) {
            return null;
        }
        let idx = 0;
        if (membershipPermissions.general.canView) {
            idx++;
        }
        if (membershipPermissions.accounts.canView) {
            idx++;
        }
        if (membershipPermissions.bills.canView) {
            idx++;
        }
        return idx;
    }, [
        adminUser.authorization.betaTransactionFeatures.canUse,
        membershipPermissions.general.canView,
        membershipPermissions.accounts.canView,
        membershipPermissions.bills.canView,
    ]);

    const isBillsDevToolsTabActive =
        billsDevToolsTabIndex != null && activeIndex === billsDevToolsTabIndex;

    const displayIncomeSplit =
        membershipDetails && showIncomeSplit(membershipDetails);

    const membershipHasIncomeSplitWithTiedRules =
        membershipDetails &&
        membershipHasHadIncomeSplitWithTies(membershipDetails);
    const membershipHasIncomeSplitV0 =
        membershipDetails && membershipHasHadIncomeSplitV0(membershipDetails);
    const membershipHasPaycheckSplit =
        membershipDetails && membershipHasHadPaycheckSplit(membershipDetails);
    const paycheckSplitTabDisplayName = (() => {
        if (membershipHasIncomeSplitV0) {
            return membershipHasIncomeSplitWithTiedRules
                ? 'Income Split (Archived)'
                : 'Income Split';
        }
        if (membershipHasIncomeSplitV0 === false) {
            return 'Paycheck Split';
        }
        return 'Income Split';
    })();

    if (
        !membershipPermissions.general.canView &&
        !membershipPermissions.accounts.canView &&
        !membershipPermissions.bills.canView &&
        !membershipPermissions.goals.canView
    ) {
        return null;
    }

    return (
        <PageContentContainer {...stackProps} paddingTop={16}>
            {membershipDetails != null && (
                <TabView
                    style={{ width: '100%', marginTop: '-0.5vw' }}
                    panelContainerStyle={{
                        width: '100%',
                        padding: 0,
                        marginTop: 32,
                    }}
                    activeIndex={parseInt(selectedTabIndex, 10) || 0}
                    onTabChange={(e) => {
                        router.setParams({ dt: e.index.toString() });
                    }}
                >
                    {membershipPermissions.general.canView && (
                        <TabPanel header="Member Profile">
                            <MemberProfile
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.accounts.canView && (
                        <TabPanel header="Accounts">
                            <AccountsTabPanel
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.bills.canView && (
                        <TabPanel header={<>Cash Flow</>}>
                            <CashFlowTabPanel
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {adminUser.authorization.betaTransactionFeatures.canUse && (
                        <TabPanel header="Bills- Dev Tools">
                            <BillsDevToolsTabPanel
                                membershipDetails={membershipDetails}
                                isTabActive={isBillsDevToolsTabActive}
                            />
                        </TabPanel>
                    )}
                    {adminUser.role === AdminUserRole.SUPERADMIN &&
                        adminUser.authorization.betaTransactionFeatures
                            .canUse && (
                            <TabPanel header="Trx Search (Beta)">
                                <TransactionSearchSection
                                    membershipDetails={membershipDetails}
                                />
                            </TabPanel>
                        )}
                    {/* for now, if you can view a memberships's accounts, you can view their income split or paycheck split configs */}
                    {/* if the membership has no paycheck split configs, we will show the income split details or an empty state */}
                    {/* TODO: add specific permission for income split & direct deposit switch? */}
                    {membershipPermissions.accounts.canView &&
                        membershipHasIncomeSplitWithTiedRules && (
                            <TabPanel header="Income Split">
                                <IncomeSplitV2TabPanel
                                    membershipDetails={membershipDetails}
                                />
                            </TabPanel>
                        )}
                    {membershipPermissions.accounts.canView &&
                        !membershipHasIncomeSplitWithTiedRules &&
                        (!!membershipHasPaycheckSplit ||
                            !!membershipHasIncomeSplitV0) && (
                            <TabPanel header={paycheckSplitTabDisplayName}>
                                <IncomeSplitTabPanel
                                    membershipDetails={membershipDetails}
                                    showIncomeSplit={displayIncomeSplit ?? true}
                                />
                            </TabPanel>
                        )}
                    {membershipPermissions.accounts.canView && (
                        <TabPanel header="Dir Dep">
                            <DirectDepositSwitchTabPanel
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.goals.canView && (
                        <TabPanel header="Goals">
                            <MembershipGoalsInfo
                                goalGroups={membershipDetails.goalGroups}
                                goalCategories={
                                    membershipDetails.allGoalCategories
                                }
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.guidanceSessions.canView && (
                        <TabPanel header="Guidance Sessions">
                            <YStack width="100%">
                                <CheckInSessionsTable
                                    membershipDetails={membershipDetails}
                                    guidanceSessions={
                                        membershipDetails.guidanceSession
                                    }
                                />
                                <GuidanceSessionsTable
                                    membershipDetails={membershipDetails}
                                    guidanceSessions={
                                        membershipDetails.guidanceSession
                                    }
                                />
                            </YStack>
                        </TabPanel>
                    )}
                    {membershipPermissions.bankOps.canUse && (
                        <TabPanel header="Banking Ops">
                            <MembershipBankingOps
                                members={membershipDetails.users}
                            />
                        </TabPanel>
                    )}
                    {!membershipPermissions.bankOps.canUse &&
                        membershipPermissions.accounts.canView && (
                            <TabPanel header="Recurring transfers">
                                <MembershipRecurringTransfers
                                    membershipDetails={membershipDetails}
                                />
                            </TabPanel>
                        )}
                    {membershipPermissions.accounts.canView && (
                        <TabPanel header="Conversational MM">
                            {membershipDetails?.id != null && (
                                <ConversationalMMSection
                                    membershipId={membershipDetails.id}
                                />
                            )}
                        </TabPanel>
                    )}
                    {membershipPermissions.accounts.canView && (
                        <TabPanel header="Money Map">
                            <MoneyMapTable />
                        </TabPanel>
                    )}
                    {adminUser.authorization.complianceOpsFunctionality
                        .canUse && (
                        <TabPanel header="Compliance">
                            <ComplianceNotesTab
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.accounts.canView &&
                        membershipHasIncomeSplitWithTiedRules &&
                        (!!membershipHasPaycheckSplit ||
                            !!membershipHasIncomeSplitV0) && (
                            <TabPanel header={paycheckSplitTabDisplayName}>
                                <IncomeSplitTabPanel
                                    membershipDetails={membershipDetails}
                                    showIncomeSplit={displayIncomeSplit ?? true}
                                />
                            </TabPanel>
                        )}
                    {canShowGuideMessaging && (
                        <TabPanel header="Document Vault">
                            <MembershipDocumentVaultTabPanel
                                membershipDetails={membershipDetails}
                            />
                        </TabPanel>
                    )}
                    {membershipPermissions.general.canView && (
                        <TabPanel header="User Events">
                            <MembershipUserEventsTabPanel
                                members={membershipDetails.users}
                            />
                        </TabPanel>
                    )}
                </TabView>
            )}
        </PageContentContainer>
    );
};
